import React, { useState, useEffect } from "react";
import {
  TextField,
  MenuItem,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from 'axios';
 
// Helper component for a field with bilingual label
const FormField = ({ labelEng, labelAfr, name, type = "text", isPreview, ...rest }) => (
  <TextField
    label={
      <>
        *{labelEng} / <span style={{ fontWeight: 'normal' }}>{labelAfr}</span>*
      </>
    }
    fullWidth
    margin="normal"
    name={name}
    type={type}
    size="small"
    InputLabelProps={{ shrink: true }}
    InputProps={isPreview ? { readOnly: true } : {}}
    disabled={isPreview}
    {...rest}
  />
);

const J29 = ({ matter, appointment, isPreview = false, onSaveSuccess }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    noticeLanguage: "English",
    province: "",
    estateOrCompanyNumber: "",
    publicationDate: "",
    dateOfMeeting: "",
    timeOfMeeting: "",
    placeOfMeeting: "",
    masterOffice: "",
  });

  useEffect(() => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch (e) {
        return '';
      }
    };

    const formatTime = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (e) {
        return '';
      }
    };

    const initialData = {
      // Defaults from matter object
      estateOrCompanyNumber: matter?.estateNo || '',
      masterOffice: matter?.mastersOffice || '',
      placeOfMeeting: matter?.magistrateOffice || '',
      // Default empty values for form fields
      noticeLanguage: 'English',
      province: '',
      publicationDate: '',
      dateOfMeeting: '',
      timeOfMeeting: '',
      ...(appointment && { // if a 'first' meeting appointment exists, overwrite with its data
        noticeLanguage: appointment.noticeLanguage || 'English',
        province: appointment.province || '',
        publicationDate: formatDate(appointment.publicationDate),
        dateOfMeeting: formatDate(appointment.proposedDateTime),
        timeOfMeeting: formatTime(appointment.proposedDateTime),
        placeOfMeeting: appointment.placeOfMeeting || appointment.magistrateOffice || matter?.magistrateOffice || '',
        estateOrCompanyNumber: appointment.estateOrCompanyNumber || matter?.estateNo || '',
        masterOffice: appointment.masterOffice || matter?.mastersOffice || '',
      })
    };
    setFormData(initialData);
  }, [matter, appointment]);

  const handleChange = (e) => {
    if (isPreview) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!matter?.id) {
      setSnackbar({ open: true, message: 'Cannot save: Legal Matter ID is missing.', severity: 'error' });
      return;
    }

    const proposedDateTime = formData.dateOfMeeting && formData.timeOfMeeting ? `${formData.dateOfMeeting}T${formData.timeOfMeeting}:00.000Z` : null;
    if (!proposedDateTime) {
      setSnackbar({ open: true, message: 'Date and Time of Meeting are required.', severity: 'error' });
      return;
    }

    const saveData = {
      ...formData, // Send all form fields
      proposedDateTime,
      dueDateTime: proposedDateTime, // Assume due date is same as proposed
      type: 'first', // This form is for the first meeting
      meetingName: 'First Meeting of Creditors',
      legalMatterId: matter.id,
    };

    try {
      const url = appointment?.id ? `https://ipms.insollex.com/api/appointments/${appointment.id}` : 'https://ipms.insollex.com/api/appointments';
      const method = appointment?.id ? 'put' : 'post';
      await axios[method](url, saveData);
      setSnackbar({ open: true, message: 'Form J29 data saved successfully!', severity: 'success' });
      if (onSaveSuccess) onSaveSuccess();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while saving.';
      setSnackbar({ open: true, message: `Error: ${errorMessage}`, severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Paper
      elevation={isPreview ? 0 : 3}
      sx={{
        p: 3,
        m: 0,
        opacity: isPreview ? 0.9 : 1,
        border: isPreview ? "1px solid #ddd" : "none",
        pointerEvents: isPreview ? "none" : "auto",
        maxWidth: 800,
        mx: 'auto'
      }}
    >
      <Box sx={{ fontFamily: "Times New Roman, serif" }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
            FORM J29
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            FIRST MEETINGS OF CREDITORS
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ textAlign: "justify", mb: 3, p: 1, border: '1px solid #eee', bgcolor: '#f9f9f9' }}>
          Notice is hereby given that the first meeting of creditors in the sequestration/liquidation estates listed below
          will be held for the proof of claims against the estates and for receiving directions concerning the administration thereof.
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
          * Mandatory Fields / Verpligte Velde
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="*Notice Language / Taal van kennisgewing"
              fullWidth
              margin="normal"
              name="noticeLanguage"
              value={formData.noticeLanguage}
              onChange={handleChange}
              disabled={isPreview}
              size="small"
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Afrikaans">Afrikaans</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Province"
              labelAfr="Provinsie"
              name="province"
              value={formData.province}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Estate/Company Number"
              labelAfr="Boedel-/Maatskappynommer"
              name="estateOrCompanyNumber"
              value={formData.estateOrCompanyNumber}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Gazette Publication Date"
              labelAfr="Publikasiedatum in Staatskoerant"
              name="publicationDate"
              type="date"
              value={formData.publicationDate}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, borderBottom: '1px solid #ccc' }}>
              Meeting Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Date of Meeting"
              labelAfr="Datum van Vergadering"
              name="dateOfMeeting"
              type="date"
              value={formData.dateOfMeeting}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Time of Meeting"
              labelAfr="Tyd van Vergadering"
              name="timeOfMeeting"
              type="time"
              value={formData.timeOfMeeting}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Place of Meeting"
              labelAfr="Plek van Vergadering"
              name="placeOfMeeting"
              value={formData.placeOfMeeting}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              labelEng="Master’s Office"
              labelAfr="Meesterskantoor"
              name="masterOffice"
              value={formData.masterOffice}
              onChange={handleChange}
              isPreview={isPreview}
            />
          </Grid>
        </Grid>

        {!isPreview && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save / Submit Form J29
            </Button>
          </Box>
        )}
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default J29;
